import { PlusIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useSWRConfig } from 'swr';

import { BaseButton } from '../../../components/button/BaseButton';
import { addOrdersToInvoice, isInvoiceListKey } from '../models';
import { OrderSelect } from './OrderSelect';
import { Invoice } from '../pages/sale/details';
import { Input } from '../../../components/input/Input';
import { Button } from '../../../components/button/Button';
import toast from 'react-hot-toast';
import { getDisplayError } from '../../../utils/get-display-error';
import { captureException } from '@sentry/react';

export interface IAddOrdersToInvoiceDialogProps {
  invoice: Invoice;
}

export const AddOrdersToInvoiceDialog: React.FC<IAddOrdersToInvoiceDialogProps> = (props) => {
  const { invoice } = props;
  const { mutate } = useSWRConfig();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild disabled={isLoading}>
        <BaseButton color="default" iconLeft={<PlusIcon className="button-icon" />} isLoading={isLoading}>
          Voeg orders toe
        </BaseButton>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />

        <Dialog.Content className="dialog-content max-w-[100vw]">
          <Dialog.Title className="heading-one mb-4">Voeg orders to aan deze factuur</Dialog.Title>

          <div className="mb-4">
            <Input
              type="text"
              placeholder={searchValue}
              value={searchValue}
              onChange={setSearchValue}
              iconLeft={<MagnifyingGlassIcon className="input-icon" />}
            />
          </div>

          <div className="h-[50vh]">
            <OrderSelect
              customerId={invoice.customer.id}
              searchValue={searchValue}
              selectedOrderIds={selectedOrderIds}
              onSelectOrder={(o) => {
                setSelectedOrderIds((prev) => {
                  if (prev.includes(o.id)) {
                    return prev.filter((id) => id !== o.id);
                  } else {
                    return [...prev, o.id];
                  }
                });
              }}
            />
          </div>

          <div className="flex justify-end">
            <Button
              color="primary"
              isLoading={isLoading}
              onTrigger={async () => {
                setIsLoading(true);
                try {
                  await addOrdersToInvoice(invoice.id, selectedOrderIds);
                  await mutate(isInvoiceListKey);
                  await mutate(['invoice', invoice.id]);
                  toast.success('Orders zijn toegevoegd aan de factuur');
                  setOpen(false);
                } catch (err) {
                  captureException(err);
                  toast.error('Kon orders niet toevoegen aan de factuur: ' + getDisplayError(err));
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Voeg orders toe
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
